/* Lavender Yield Tracker — vanilla JS, no build.
   Embedded in the Shopify storefront the theme sets window.LAVENDER_PROXY (same-origin
   App Proxy base, signed customer identity). Standalone (localhost) same-origin '' is
   used with DEV_FAKE_CUSTOMER_ID on the backend for testing account flows. */

const PROXY = window.LAVENDER_PROXY || '';

const $ = (id) => document.getElementById(id);
const plantTypeEl = $('lytPlantType');
const materialAmtEl = $('lytMaterialAmt');
const materialUnitEl = $('lytMaterialUnit');
const distillMinEl = $('lytDistillMin');
const yieldAmtEl = $('lytYieldAmt');
const yieldUnitEl = $('lytYieldUnit');
const runDateEl = $('lytRunDate');
const runTimeEl = $('lytRunTime');
const runPeriodEl = $('lytRunPeriod');
const budsPctEl = $('lytBudsPct');
const elementEl = $('lytElement');
const powerPctEl = $('lytPowerPct');
const settleEl = $('lytSettle');
const hydroAmtEl = $('lytHydroAmt');
const hydroUnitEl = $('lytHydroUnit');
const notesEl = $('lytNotes');

// Default the date to today (local), like a paper logbook would.
if (!runDateEl.value) runDateEl.value = new Date().toLocaleDateString('en-CA');
const resultsEl = $('lytResults');
const saveBtn = $('lytSaveBtn');
const signInEl = $('lytSignIn');
const runsSection = $('lytRuns');
const runsBody = $('lytRunsBody');
const runsEmpty = $('lytRunsEmpty');
const toastEl = $('lytToast');

// ── units ────────────────────────────────────────────────────────────
const WEIGHT_LABEL = { lb: 'lb', oz: 'oz', kg: 'kg', g: 'g' };
const WEIGHT_TO_KG = { lb: 0.45359237, oz: 0.028349523125, kg: 1, g: 0.001 };
const VOL_LABEL = { floz: 'fl oz', ml: 'mL', l: 'L', qt: 'qt', gal: 'gal' };
const VOL_TO_ML = { floz: 29.5735295625, ml: 1, l: 1000, qt: 946.352946, gal: 3785.411784 };

/** "8:30" / "8" + "PM" → 24h "HH:MM" for storage; null if blank or unparseable. */
function parseTime12(text, period) {
  const m = String(text).trim().match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? '0');
  if (h < 1 || h > 12 || min > 59) return null;
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Settling minutes → "30 min" / "24 hr" for display. */
function fmtSettle(min) {
  if (min === null || min === undefined) return null;
  return min < 60 ? `${min} min` : `${min / 60} hr`;
}

/** Stored 24h "HH:MM" → "7:45 AM" for display. */
function fmtTime(hhmm) {
  if (!hhmm) return null;
  const [h24, min] = hhmm.split(':').map(Number);
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h = h24 % 12 || 12;
  return `${h}:${String(min).padStart(2, '0')} ${period}`;
}

const fmt = (n) => {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  if (n >= 100) return String(Math.round(n));
  if (n >= 1) return String(Number(n.toFixed(2)));
  return String(Number(n.toPrecision(3)));
};

function readInputs() {
  return {
    plantType: plantTypeEl.value,
    materialAmount: Number(materialAmtEl.value),
    materialUnit: materialUnitEl.value,
    distillMinutes: Number(distillMinEl.value),
    yieldAmount: Number(yieldAmtEl.value),
    yieldUnit: yieldUnitEl.value,
    runDate: runDateEl.value || null,
    runTime: parseTime12(runTimeEl.value, runPeriodEl.value),
    runTimeText: runTimeEl.value.trim(),
    budsOpenPct: budsPctEl.value === '' ? null : Number(budsPctEl.value),
    elementWatts: elementEl.value === '' ? null : Number(elementEl.value),
    powerPct: powerPctEl.value === '' ? null : Number(powerPctEl.value),
    settleMinutes: settleEl.value === '' ? null : Number(settleEl.value),
    hydrosolAmount: Number(hydroAmtEl.value) > 0 ? Number(hydroAmtEl.value) : null,
    hydrosolUnit: Number(hydroAmtEl.value) > 0 ? hydroUnitEl.value : null,
    notes: notesEl ? notesEl.value.trim() : '',
  };
}

function isComplete(v) {
  return (
    v.plantType &&
    v.materialAmount > 0 &&
    v.distillMinutes > 0 &&
    v.yieldAmount > 0
  );
}

/** The three derived numbers for a run: ratio in the run's own units, mL/kg, per-hour rate. */
function derive(v) {
  const ratio = v.yieldAmount / v.materialAmount;
  const mlPerKg =
    (v.yieldAmount * VOL_TO_ML[v.yieldUnit]) / (v.materialAmount * WEIGHT_TO_KG[v.materialUnit]);
  const perHour = v.yieldAmount / (v.distillMinutes / 60);
  return { ratio, mlPerKg, perHour };
}

function renderResults() {
  const v = readInputs();
  if (!isComplete(v)) {
    resultsEl.hidden = true;
    saveBtn.disabled = true;
    return;
  }
  const d = derive(v);
  const yu = VOL_LABEL[v.yieldUnit];
  const wu = WEIGHT_LABEL[v.materialUnit];
  $('lytRatioMain').textContent = `${fmt(d.ratio)} ${yu} / ${wu}`;
  $('lytRatioNorm').textContent = `${fmt(d.mlPerKg)} mL / kg`;
  $('lytRatioRate').textContent = `${fmt(d.perHour)} ${yu} / hr`;
  const hasHydro = v.hydrosolAmount !== null;
  $('lytHydroTile').hidden = !hasHydro;
  if (hasHydro) {
    $('lytRatioHydro').textContent =
      `${fmt(v.hydrosolAmount / v.materialAmount)} ${VOL_LABEL[v.hydrosolUnit]} / ${wu}`;
  }
  const buds = v.budsOpenPct === null ? '' : ` (${fmt(v.budsOpenPct)}% buds open)`;
  const whenBits = [];
  if (v.runDate) {
    whenBits.push(new Date(v.runDate + 'T00:00:00').toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    }));
  }
  if (v.runTime) whenBits.push(fmtTime(v.runTime));
  const when = whenBits.length ? `${whenBits.join(' ')} — ` : '';
  const hydro = hasHydro ? ` and ${fmt(v.hydrosolAmount)} ${VOL_LABEL[v.hydrosolUnit]} of hydrosol` : '';
  $('lytResultCaption').textContent =
    `${when}${v.plantType}${buds}: ${fmt(v.materialAmount)} ${wu} of plant material distilled for ` +
    `${fmt(v.distillMinutes)} min yielded ${fmt(v.yieldAmount)} ${yu} of oil${hydro}.`;
  resultsEl.hidden = false;
  saveBtn.disabled = !loggedIn;
}

['input', 'change'].forEach((ev) => {
  [plantTypeEl, materialAmtEl, materialUnitEl, distillMinEl, yieldAmtEl, yieldUnitEl,
   budsPctEl, runDateEl, runTimeEl, runPeriodEl, settleEl, hydroAmtEl, hydroUnitEl].forEach((el) =>
    el.addEventListener(ev, renderResults),
  );
});

// ── toast ────────────────────────────────────────────────────────────
let toastTimer = null;
function toast(msg) {
  toastEl.textContent = msg;
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toastEl.hidden = true), 3200);
}

// ── account state + saved runs ───────────────────────────────────────
let loggedIn = false;

async function api(path, opts) {
  const res = await fetch(PROXY + path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
  return body;
}

function runRow(run) {
  const d = derive(run);
  const tr = document.createElement('tr');
  const dateFmt = { year: 'numeric', month: 'short', day: 'numeric' };
  // Prefer the logged run date (exact text, parsed as local midnight); fall back to saved-at.
  const when = run.runDate
    ? new Date(run.runDate + 'T00:00:00').toLocaleDateString(undefined, dateFmt)
    : new Date(run.createdAt).toLocaleDateString(undefined, dateFmt);
  const cells = [
    when,
    fmtTime(run.runTime) || '—',
    run.plantType,
    run.budsOpenPct === null || run.budsOpenPct === undefined ? '—' : `${fmt(run.budsOpenPct)}%`,
    run.elementWatts === null || run.elementWatts === undefined ? '—' : `${fmt(run.elementWatts)} W`,
    run.powerPct === null || run.powerPct === undefined ? '—' : `${fmt(run.powerPct)}%`,
    `${fmt(run.materialAmount)} ${WEIGHT_LABEL[run.materialUnit]}`,
    `${fmt(run.distillMinutes)} min`,
    fmtSettle(run.settleMinutes) || '—',
    `${fmt(run.yieldAmount)} ${VOL_LABEL[run.yieldUnit]}`,
    run.hydrosolAmount === null || run.hydrosolAmount === undefined
      ? '—'
      : `${fmt(run.hydrosolAmount)} ${VOL_LABEL[run.hydrosolUnit]}`,
    `${fmt(d.ratio)} ${VOL_LABEL[run.yieldUnit]}/${WEIGHT_LABEL[run.materialUnit]}`,
  ];
  for (const c of cells) {
    const td = document.createElement('td');
    td.textContent = c;
    tr.appendChild(td);
  }
  const tdNotes = document.createElement('td');
  tdNotes.className = 'lyt-notes-cell';
  tdNotes.textContent = run.notes || '';
  tr.appendChild(tdNotes);
  const tdDel = document.createElement('td');
  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'lyt-del';
  del.title = 'Delete this run';
  del.textContent = '×';
  del.addEventListener('click', async () => {
    if (!confirm('Delete this run?')) return;
    try {
      await api(`/api/lavender/runs/${run.id}`, { method: 'DELETE' });
      tr.remove();
      if (!runsBody.children.length) runsEmpty.hidden = false;
    } catch (err) {
      toast(err.message);
    }
  });
  tdDel.appendChild(del);
  tr.appendChild(tdDel);
  return tr;
}

async function loadRuns() {
  try {
    const { runs } = await api('/api/lavender/runs');
    runsBody.textContent = '';
    runs.forEach((r) => runsBody.appendChild(runRow(r)));
    runsEmpty.hidden = runs.length > 0;
    runsSection.hidden = false;
  } catch {
    runsSection.hidden = true;
  }
}

saveBtn.addEventListener('click', async () => {
  const v = readInputs();
  if (!isComplete(v)) return;
  // A typed-but-unparseable time should block the save loudly, not vanish silently.
  if (v.runTimeText && v.runTime === null) {
    toast('Time of day should look like 8:30 (then pick AM or PM).');
    return;
  }
  delete v.runTimeText;
  saveBtn.disabled = true;
  try {
    const { run } = await api('/api/lavender/runs', {
      method: 'POST',
      body: JSON.stringify({ ...v, notes: v.notes || null }),
    });
    runsBody.prepend(runRow(run));
    runsEmpty.hidden = true;
    runsSection.hidden = false;
    toast('Run saved to your account.');
  } catch (err) {
    toast(err.message);
    // A 401 here means the session lapsed since boot — flip the UI back to
    // signed-out so the sign-in link reappears instead of a dead Save button.
    refreshAuth();
  } finally {
    saveBtn.disabled = false;
  }
});

async function refreshAuth() {
  const was = loggedIn;
  // The theme stamps whether the STOREFRONT session was signed in at render;
  // forwarding it on the /api/me call lets the backend log both sides of a
  // session mismatch (diagnostic only — the App Proxy signature stays the
  // only identity source).
  const stamped = document.getElementById('lavender-tracker-root')?.dataset.storefrontAuth;
  try {
    const me = await api('/api/me' + (stamped !== undefined ? `?sfa=${stamped}` : ''));
    loggedIn = Boolean(me.loggedIn && me.accountsEnabled);
  } catch {
    loggedIn = false;
  }
  signInEl.hidden = loggedIn;
  const hintEl = $('lytSignInHint');
  if (hintEl) hintEl.hidden = loggedIn;
  saveBtn.hidden = !loggedIn;
  if (loggedIn && !was) {
    loadRuns();
    // They just came back from signing in (new tab) — confirm it landed.
    if (refreshAuth._sawAnon) toast('Signed in — your runs will save to your account.');
  }
  if (!loggedIn) {
    runsSection.hidden = true;
    refreshAuth._sawAnon = true;
  }
  renderResults();
  let tryTs = 0;
  try { tryTs = Number(sessionStorage.getItem('lyt:signinTry') || 0); } catch (e) {}
  if (loggedIn) {
    try { sessionStorage.removeItem('lyt:signinTry'); } catch (e) {}
    const r = $('lytResetRow');
    if (r) r.remove();
  } else if (tryTs && Date.now() - tryTs < 15 * 60 * 1000) {
    recoveryRow();
  }
  console.info('[lavender] storefrontAuth=' + stamped + ' apiLoggedIn=' + loggedIn);
  // "Storefront signed-in but API anonymous" — retry once (binding can lag
  // right after the hosted-accounts login).
  if (stamped === '1' && !loggedIn && !refreshAuth._retried) {
    refreshAuth._retried = true;
    setTimeout(refreshAuth, 1500);
  }
}

// Track sign-in attempts so a failed round trip can offer session recovery.
signInEl.addEventListener('click', () => {
  try { sessionStorage.setItem('lyt:signinTry', String(Date.now())); } catch (e) {}
});

/** The "signed in at the portal but the store doesn't know you" recovery row. */
function recoveryRow() {
  let el = $('lytResetRow');
  if (!el) {
    el = document.createElement('p');
    el.id = 'lytResetRow';
    el.className = 'lyt-auth lyt-reset';
    el.innerHTML = 'Signed in but not recognized? ' +
      '<a class="lyt-signin" href="/customer_authentication/redirect?locale=en">Try the store sign-in</a>' +
      '<span class="lyt-signin-hint"> \u2014 one tap, you\u2019ll land right back here. Then tap \u201cSign in\u201d again.</span>';
    const anchor = signInEl.closest('.lyt-auth') || signInEl.parentElement;
    anchor.after(el);
  }
  return el;
}

refreshAuth();

// The store uses Shopify's hosted customer accounts: signing in happens on
// account.<shop> and drops the customer back here via history/back or a tab
// switch — both of which can serve this page from bfcache with stale
// signed-out state. Re-check whenever the page is shown again.
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshAuth(); });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && !loggedIn) refreshAuth();
});
